import { useEffect, useRef } from "react";
import { resolveNextSelection } from "./formContainerListSelection";

interface UseActiveRecordSyncParams<T> {
  listItems: T[] | undefined;
  idKey: string;
  selectedId?: string | null;
  loading?: boolean;
  // The search term `listItems` was actually fetched with -- the one the list
  // on screen belongs to, NOT whatever was just typed/applied (the two differ
  // for the render between a filter changing and its fetch going pending). A
  // filtered list is a subset: the selection being absent from it, or the
  // list being empty, doesn't mean the record is gone. So under a filter only
  // a record that drops out of a list refreshed with the SAME filter -- i.e. a
  // delete -- is acted on. Note an edit that stops the record matching the
  // filter (e.g. renaming it) looks the same from here and is treated as a
  // delete too: the record can no longer be reached from that list, so moving
  // on to one that can is the least surprising outcome.
  filter?: string;
  onSelect: (item: T) => void;
  onEmpty: () => void;
}

// Shared fix for the "stale active record" bug found repeatedly across the
// app (Vulnerability, Asset/Liability/Policy, System Manager, Action List):
// a list correctly refreshes after a delete, but a separately-held "active
// record" only gets re-derived by an id-gated effect (a route param, a row
// selection object) that a delete/refresh doesn't necessarily touch. This
// re-checks the active record against the CURRENT list on every list change,
// and asks the caller to select a fallback record or clear out, exactly the
// way formContainerListSelection.ts's resolveNextSelection already does for
// FormContainerWithList -- generalized so any module can reuse the same rule
// instead of hand-rolling its own (and getting it wrong, or forgetting it).
export function useActiveRecordSync<T extends Record<string, any>>({
  listItems,
  idKey,
  selectedId,
  loading,
  filter = "",
  onSelect,
  onEmpty,
}: UseActiveRecordSyncParams<T>) {
  // Whether a load of this list has actually been observed. An empty list on
  // the very first render means "the request hasn't come back yet", not "there
  // are no records" -- and those need opposite responses. Treating the two the
  // same is what broke a page refresh: `onEmpty` fired before the list arrived,
  // stripping the record id out of the URL, and once it was gone the list
  // landed with nothing selected and the hook redirected to the FIRST record.
  const hasLoadedRef = useRef(false);
  // The last settled list and the filter it was fetched with, so a filtered
  // list can tell "the selected record was deleted" apart from "the selected
  // record just isn't in these search results".
  const prevRef = useRef<{ filter: string; items: T[] | undefined } | null>(
    null,
  );

  useEffect(() => {
    if (loading) {
      hasLoadedRef.current = true;
      return;
    }

    const listHasItems = Array.isArray(listItems) && listItems.length > 0;
    if (listHasItems) hasLoadedRef.current = true;
    // Nothing loaded yet: leave the URL (and the selection) alone.
    if (!listHasItems && !hasLoadedRef.current) return;

    const hasSelection = selectedId != null && selectedId !== "";
    const prev = prevRef.current;
    prevRef.current = { filter, items: listItems };

    if (filter !== "") {
      const wasInPrevList =
        hasSelection &&
        prev?.filter === filter &&
        Array.isArray(prev.items) &&
        prev.items.some((item) => item[idKey] === selectedId);
      const isInList =
        listHasItems && listItems!.some((item) => item[idKey] === selectedId);
      // Not a delete -- a new search (or a no-match one) just doesn't include
      // the record. Leave the selection, and the page, alone.
      if (!wasInPrevList || isInList) return;
    }

    const result = resolveNextSelection(listItems, idKey, selectedId, hasSelection);

    if (result.type === "empty") {
      onEmpty();
      return;
    }

    const next = (listItems as T[])[result.index];
    // Only act when the current selection is actually gone (or there wasn't
    // one) -- resolveNextSelection returns a valid index even when the
    // existing selection is still present, so this avoids re-selecting (and
    // potentially re-navigating/re-dispatching) on every render.
    if (!hasSelection || next?.[idKey] !== selectedId) {
      onSelect(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listItems, idKey, selectedId, loading, filter]);
}
